import { ReactNode } from 'react';

import {
  Awareness,
  Bell,
  ClosedAccount,
  Commission,
  Dashboard,
  Finance,
  Notification,
  Play,
  Reminder,
  Shield,
  Union,
  Users,
} from 'assets/icons';
import { SidebarItems, routes } from 'enums';
import { SMS_DELIVERY_PAGE_ENABLED } from 'config/features';

/**
 * The navigation, described once.
 *
 * The desktop sidebar and the burger menu used to each hold their own copy of
 * this list, written out by hand as eleven and seven near-identical JSX blocks.
 * They had already drifted: the burger menu was missing Awareness cards,
 * Admins, App content and Intro & onboarding videos, so on a narrow viewport
 * four sections of the console were simply unreachable. A shared array makes
 * that class of drift impossible -- adding a page here adds it to both.
 */
export interface NavItem {
  /** Label, and the value the active check compares against. */
  title: SidebarItems;
  /** Where the item navigates to. */
  route: string;
  /** Rendered with `active` so the icon can match the label's colour. */
  icon: (active: boolean) => ReactNode;
  /**
   * True for the one item that carries the pending-approvals count.
   *
   * A flag rather than the number itself: this module is a static description
   * of the navigation, and the count is live state that belongs to whoever
   * renders it.
   */
  showsPendingBadge?: boolean;
  /**
   * Extra paths that should light this item up.
   *
   * A provider's detail page lives at `/doctor/:id`, which is a different
   * route from the `/doctors` list but is unmistakably the same section of the
   * console -- so the nav should say so rather than going blank.
   */
  alsoMatches?: string[];
}

const ALL_NAV_ITEMS: NavItem[] = [
  {
    title: SidebarItems.DASHBOARD,
    route: routes.MAIN.DASHBOARD,
    icon: active => <Dashboard active={active} />,
  },
  {
    title: SidebarItems.USERS,
    route: routes.MAIN.USERS,
    icon: active => <Users active={active} />,
    alsoMatches: [routes.MAIN.USER],
  },
  {
    title: SidebarItems.DOCTORS_NURSES,
    route: routes.MAIN.DOCTORS,
    icon: active => <Union active={active} />,
    showsPendingBadge: true,
    alsoMatches: [routes.MAIN.DOCTOR],
  },
  {
    title: SidebarItems.FINANCE,
    route: routes.MAIN.FINANCES,
    icon: active => <Finance active={active} />,
  },
  {
    title: SidebarItems.DAILY_USERS_REMINDER,
    route: routes.MAIN.ARTICLES,
    icon: active => <Reminder active={active} />,
  },
  {
    title: SidebarItems.AWARENESS_CARDS,
    route: routes.MAIN.AWARENESS,
    icon: active => <Awareness active={active} />,
  },
  {
    title: SidebarItems.URGENT_NOTIFICATION,
    route: routes.MAIN.NOTIFICATION,
    icon: active => <Notification active={active} />,
  },
  {
    title: SidebarItems.COMMISSION_RATES_PRICING,
    route: routes.MAIN.COMMISSION,
    icon: active => <Commission active={active} />,
  },
  {
    title: SidebarItems.ADMINS,
    route: routes.MAIN.ADMINS,
    icon: active => <Shield active={active} />,
  },
  {
    title: SidebarItems.APP_CONTENT,
    route: routes.MAIN.CONTENT,
    icon: active => <Reminder active={active} />,
  },
  {
    title: SidebarItems.ONBOARDING_VIDEOS,
    route: routes.MAIN.ONBOARDING_VIDEOS,
    icon: active => <Play active={active} />,
  },
  {
    title: SidebarItems.SMS_DELIVERY,
    route: routes.MAIN.SMS_DELIVERY,
    icon: active => <Bell active={active} />,
  },
  // Beside the other SMS entry rather than with the user pages, because the
  // two answer the same question from opposite ends: that one asks whether the
  // channel is working at all, this one asks what happened to one person's
  // code. An operator who opens the wrong one is one click from the right one.
  {
    title: SidebarItems.SMS_CODES,
    route: routes.MAIN.SMS_CODES,
    icon: active => <Bell active={active} />,
  },
  // Last, because it is the page you go to after the fact rather than one you
  // work from. It is also the only place the console can name somebody whose
  // account row no longer can.
  {
    title: SidebarItems.ACCOUNT_DELETIONS,
    route: routes.MAIN.ACCOUNT_DELETIONS,
    icon: active => <ClosedAccount active={active} />,
  },
];

/**
 * The items actually rendered.
 *
 * A hidden page is filtered out here rather than commented out above, so its
 * label, icon and position survive intact and it returns by flipping one
 * constant. The route is gated by the same constant in `Router.tsx` -- hiding
 * only the sidebar entry would leave the page reachable by typing the URL.
 */
export const NAV_ITEMS: NavItem[] = ALL_NAV_ITEMS.filter(
  item => SMS_DELIVERY_PAGE_ENABLED || item.title !== SidebarItems.SMS_DELIVERY,
);

/**
 * Which nav item, if any, the current path belongs to.
 *
 * Selection used to be a `useState` seeded with a hardcoded default, so it
 * described the last click rather than the current page: a reload on
 * /commission highlighted Dashboard, and because the hidden burger menu kept
 * its own state with a different default, two items claimed to be active at
 * once. Reading the URL means the nav cannot disagree with the address bar --
 * including after a reload, a deep link, or the browser back button.
 *
 * Prefixes are matched on a path boundary so `/user` never claims `/users`.
 */
export const activeNavTitle = (pathname: string): SidebarItems | undefined =>
  NAV_ITEMS.find(item =>
    [item.route, ...(item.alsoMatches ?? [])].some(
      path => pathname === path || pathname.startsWith(`${path}/`),
    ),
  )?.title;
